import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { goalsApi, initiativesApi, unitGoalsApi } from '../services/apiService';
import { StrategicGoalList, InitiativeList, UnitGoalList } from '../types';
import { COLORS, FONTS } from '../constants/config';
import { MainStackParamList } from '../navigation/MainNavigator';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const SearchCheckIcon = ({ size = 40, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M11 18C14.866 18 18 14.866 18 11C18 7.13401 14.866 4 11 4C7.13401 4 4 7.13401 4 11C4 14.866 7.13401 18 11 18Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M16 16L20 20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M8 11L10 13L14 9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ChevronLeftIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);


// ============================================================
// Props Interface
// ============================================================

interface PendingApprovalsCardProps {
  organizationId: number;
  onApprove?: (type: 'goal' | 'initiative' | 'unitGoal', id: number) => Promise<void>;
  onReject?: (type: 'goal' | 'initiative' | 'unitGoal', id: number) => Promise<void>;
}

// ============================================================
// Main Component
// ============================================================

export default function PendingApprovalsCard({ organizationId, onApprove, onReject }: PendingApprovalsCardProps) {
  const navigation = useNavigation<NavigationProp>();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'goals' | 'initiatives' | 'unitGoals'>('goals');
  const [loading, setLoading] = useState(true);
  const [pendingGoals, setPendingGoals] = useState<StrategicGoalList[]>([]);
  const [pendingInitiatives, setPendingInitiatives] = useState<InitiativeList[]>([]);
  const [pendingUnitGoals, setPendingUnitGoals] = useState<UnitGoalList[]>([]);

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    backgroundAlt: colors.backgroundAlt,
    iconDefault: colors.iconDefault,
    tabInactiveText: isDark ? '#9CA3AF' : '#334155',
  };

  const fetchPendingItems = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch pending goals, initiatives, and unit goals for this organization
      const [goalsResponse, initiativesResponse, unitGoalsResponse] = await Promise.all([
        goalsApi.list({ department_id: organizationId, status: 'pending' }),
        initiativesApi.list({ organization: organizationId, approval_status: 'pending' }),
        unitGoalsApi.list({ approval_status: 'pending' }),
      ]);

      setPendingGoals(goalsResponse.results || []);
      setPendingInitiatives(initiativesResponse.results || []);
      setPendingUnitGoals(unitGoalsResponse.results || []);
    } catch (error) {
      console.error('Error fetching pending items:', error);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchPendingItems();
  }, [fetchPendingItems]);

  const handleViewGoalDetails = (goalId: number) => {
    navigation.navigate('GoalDetail', { id: goalId });
  };

  const handleViewInitiativeDetails = (initiativeId: number) => {
    navigation.navigate('InitiativeDetail', { id: initiativeId });
  };

  const handleViewUnitGoalDetails = (unitGoalId: number) => {
    navigation.navigate('UnitGoalDetail', { id: unitGoalId });
  };

  const currentItems = activeTab === 'goals' ? pendingGoals : activeTab === 'initiatives' ? pendingInitiatives : pendingUnitGoals;
  const totalPending = pendingGoals.length + pendingInitiatives.length + pendingUnitGoals.length;

  // Don't render if no pending items
  if (!loading && totalPending === 0) {
    return null;
  }

  return (
    <View style={[styles.container, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Text style={[styles.headerTitle, { color: themeColors.textPrimary }]}>{t('approvals.pendingGoalsAndInitiatives')}</Text>
        </View>
        <SearchCheckIcon size={40} color={themeColors.iconDefault} />
      </View>

      {/* Tabs */}
      <View style={[styles.tabsContainer, { borderBottomColor: themeColors.cardBorder }]}>
        <View style={styles.tabsRow}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'unitGoals' && styles.tabInactive]}
            onPress={() => setActiveTab('unitGoals')}
          >
            <Text style={[styles.tabText, activeTab === 'unitGoals' && { color: themeColors.tabInactiveText }]}>
              {t('approvals.unitGoalsForApproval')}
              {pendingUnitGoals.length > 0 && ` (${pendingUnitGoals.length})`}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'initiatives' && styles.tabInactive]}
            onPress={() => setActiveTab('initiatives')}
          >
            <Text style={[styles.tabText, activeTab === 'initiatives' && { color: themeColors.tabInactiveText }]}>
              {t('approvals.initiativesForApproval')}
              {pendingInitiatives.length > 0 && ` (${pendingInitiatives.length})`}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'goals' && styles.tabActive]}
            onPress={() => setActiveTab('goals')}
          >
            <Text style={[styles.tabText, activeTab === 'goals' && styles.tabTextActive]}>
              {t('approvals.goalsForApproval')}
              {pendingGoals.length > 0 && ` (${pendingGoals.length})`}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Content Area */}
      <View style={[styles.contentArea, { backgroundColor: themeColors.backgroundAlt }]}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color="#A6813F" />
          </View>
        ) : currentItems.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>
              {activeTab === 'goals'
                ? t('approvals.noGoalsPending')
                : activeTab === 'initiatives'
                ? t('approvals.noInitiativesPending')
                : t('approvals.noUnitGoalsPending')}
            </Text>
          </View>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.cardsScrollContent}
            style={styles.cardsScrollView}
          >
            {activeTab === 'goals'
              ? pendingGoals.map((goal) => (
                  <View key={goal.id} style={[styles.itemCard, { backgroundColor: themeColors.card }]}>
                    {/* Card Title */}
                    <Text style={[styles.cardTitle, { color: themeColors.textPrimary }]} numberOfLines={2}>
                      {goal.title}
                    </Text>

                    {/* Status Badge */}
                    <View style={styles.statusBadge}>
                      <Text style={styles.statusBadgeText}>{t('approvals.pendingApproval')}</Text>
                    </View>

                    {/* View Details Link */}
                    <TouchableOpacity
                      style={styles.viewDetailsLink}
                      onPress={() => handleViewGoalDetails(goal.id)}
                    >
                      <ChevronLeftIcon size={16} color={themeColors.textMuted} />
                      <Text style={[styles.viewDetailsText, { color: themeColors.textMuted }]}>{t('common.viewDetails')}</Text>
                    </TouchableOpacity>
                  </View>
                ))
              : activeTab === 'initiatives'
              ? pendingInitiatives.map((initiative) => (
                  <View key={initiative.id} style={[styles.itemCard, { backgroundColor: themeColors.card }]}>
                    {/* Card Title */}
                    <Text style={[styles.cardTitle, { color: themeColors.textPrimary }]} numberOfLines={2}>
                      {initiative.title}
                    </Text>

                    {/* Status Badge */}
                    <View style={styles.statusBadge}>
                      <Text style={styles.statusBadgeText}>{t('approvals.pendingApproval')}</Text>
                    </View>

                    {/* View Details Link */}
                    <TouchableOpacity
                      style={styles.viewDetailsLink}
                      onPress={() => handleViewInitiativeDetails(initiative.id)}
                    >
                      <ChevronLeftIcon size={16} color={themeColors.textMuted} />
                      <Text style={[styles.viewDetailsText, { color: themeColors.textMuted }]}>{t('common.viewDetails')}</Text>
                    </TouchableOpacity>
                  </View>
                ))
              : pendingUnitGoals.map((unitGoal) => (
                  <View key={unitGoal.id} style={[styles.itemCard, { backgroundColor: themeColors.card }]}>
                    {/* Card Title */}
                    <Text style={[styles.cardTitle, { color: themeColors.textPrimary }]} numberOfLines={2}>
                      {unitGoal.name}
                    </Text>

                    {/* Status Badge */}
                    <View style={styles.statusBadge}>
                      <Text style={styles.statusBadgeText}>{t('unitGoals.pendingApproval')}</Text>
                    </View>

                    {/* View Details Link */}
                    <TouchableOpacity
                      style={styles.viewDetailsLink}
                      onPress={() => handleViewUnitGoalDetails(unitGoal.id)}
                    >
                      <ChevronLeftIcon size={16} color={themeColors.textMuted} />
                      <Text style={[styles.viewDetailsText, { color: themeColors.textMuted }]}>{t('common.viewDetails')}</Text>
                    </TouchableOpacity>
                  </View>
                ))}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  container: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 20,
    gap: 20,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  headerContent: {
    flex: 1,
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },

  // Tabs
  tabsContainer: {
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    paddingVertical: 12,
  },
  tabsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  tab: {
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    borderRadius: 8,
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabActive: {
    backgroundColor: '#A6813F',
  },
  tabInactive: {
    backgroundColor: 'transparent',
  },
  tabText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    textAlign: 'center',
  },
  tabTextActive: {
    color: COLORS.white,
  },
  tabTextInactive: {
    color: '#334155',
  },

  // Content Area
  contentArea: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 16,
    minHeight: 200,
  },
  cardsScrollView: {
    transform: [{ scaleX: -1 }],
  },
  cardsScrollContent: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 4,
    paddingVertical: 4,
  },

  // Item Card - Simple Figma Design
  itemCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 16,
    width: 240,
    gap: 12,
    transform: [{ scaleX: -1 }], // Flip back to normal (parent is flipped for RTL scroll)
  },

  // Card Title
  cardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 22,
  },

  // Status Badge
  statusBadge: {
    backgroundColor: '#9467BD',
    borderRadius: 40,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignSelf: 'flex-start',
  },
  statusBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: COLORS.white,
  },

  // View Details Link
  viewDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
  },
  viewDetailsText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },

  // Loading & Empty States
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 160,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 160,
  },
  emptyText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'center',
  },
});
